/**
 * Import Wizard API client.
 * Talks to /import/* on the API Gateway.
 */

import apiClient from './client';

export type ImportSource = 'local' | 'gdrive' | 'onedrive' | 'dropbox';

export interface CloudFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  modifiedTime: string | null;
  isFolder: boolean;
  path?: string;          // dropbox uses path; gdrive/onedrive use id
  webViewLink?: string;
  iconLink?: string;
  contentHash?: string | null;
}

export interface FileRef {
  /** for cloud sources */
  fileId?: string;
  /** dropbox uses path-based addressing */
  path?: string;
  /** for local staged files */
  stagingKey?: string;
  name: string;
  size: number;
  mimeType?: string;
}

export interface ImportItem {
  id: string;
  filename: string;
  fileSizeBytes: number | null;
  sourceRef: any;
  detectedCategory: string | null;
  detectionTier: number | null;
  detectionConfidence: number | null;
  suggestedClient: { id: string; name: string } | null;
  clientOverride: { id: string; name: string } | null;
  categoryOverride: string | null;
  defaultAction: 'include' | 'skip' | 'review';
  userAction: 'include' | 'skip' | null;
  status: string;
  resultingFileId: string | null;
  errorMessage: string | null;
}

export interface ImportSession {
  id: string;
  tenant_id: string;
  source: ImportSource;
  target_client_id: string | null;
  state: 'created' | 'detecting' | 'review' | 'uploading' | 'done' | 'failed';
  totals: Record<string, number>;
}

export const importService = {
  // ─── Cloud connections + browsing ─────────────────────────────────────
  async cloudStatus(): Promise<{
    googleDrive: { connected: boolean; email?: string };
    oneDrive: { connected: boolean; email?: string };
    dropbox: { connected: boolean; email?: string };
  }> {
    // Existing endpoint returns google + onedrive
    const a = await apiClient.get('/cloud/status');
    const data = a.data?.data || {};
    // Dropbox status - query oauth_tokens directly via /cloud/status if backend
    // includes it later. For now we treat absence as not connected.
    return {
      googleDrive: data.googleDrive || { connected: false },
      oneDrive: data.oneDrive || { connected: false },
      dropbox: data.dropbox || { connected: false },
    };
  },

  async connectGoogleDrive(): Promise<string> {
    const r = await apiClient.post('/cloud/google-drive/connect', {});
    return r.data?.data?.authUrl;
  },

  async connectOneDrive(): Promise<string> {
    const r = await apiClient.post('/cloud/onedrive/connect', {});
    return r.data?.data?.authUrl;
  },

  async connectDropbox(): Promise<string> {
    const r = await apiClient.post('/import/dropbox/connect', {});
    return r.data?.data?.authUrl;
  },

  async listCloudFiles(provider: 'gdrive' | 'onedrive' | 'dropbox', path: string = '', pageToken?: string): Promise<{
    files: CloudFile[];
    nextPageToken?: string;
  }> {
    const params = new URLSearchParams({ provider, path });
    if (pageToken) params.set('pageToken', pageToken);
    const r = await apiClient.get(`/import/cloud/list?${params}`);
    return r.data?.data || { files: [], nextPageToken: undefined };
  },

  // ─── Local upload (presigned PUT to S3 staging) ───────────────────────
  async getLocalUploadUrls(files: { filename: string; size: number; mimeType?: string }[]): Promise<{
    stagingId: string;
    files: Array<{ uploadUrl: string; stagingKey: string; filename: string; size: number; mimeType: string }>;
  }> {
    const r = await apiClient.post('/import/local/upload-urls', { files });
    return r.data?.data;
  },

  async uploadLocalFile(uploadUrl: string, file: File): Promise<void> {
    await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
  },

  async registerStagedLocal(sessionId: string, files: { stagingKey: string; filename: string; size: number; mimeType: string }[]): Promise<void> {
    await apiClient.post('/import/local/register', { sessionId, files });
  },

  // ─── Session lifecycle ────────────────────────────────────────────────
  async createSession(payload: { source: ImportSource; targetClientId?: string; fileRefs: FileRef[] }): Promise<{
    sessionId: string;
    itemCount: number;
    mode: 'sync' | 'async';
    totalBytes: number;
  }> {
    const r = await apiClient.post('/import/session', payload);
    return r.data?.data;
  },

  async startDetection(sessionId: string): Promise<{ mode: 'sync' | 'async' }> {
    const r = await apiClient.post(`/import/session/${sessionId}/detect`, {});
    return r.data?.data;
  },

  async getItems(sessionId: string): Promise<{ session: ImportSession; items: ImportItem[] }> {
    const r = await apiClient.get(`/import/session/${sessionId}/items`);
    return r.data?.data;
  },

  async patchItem(sessionId: string, itemId: string, patch: { userAction?: 'include' | 'skip'; clientId?: string; categoryOverride?: string }): Promise<void> {
    await apiClient.patch(`/import/session/${sessionId}/items/${itemId}`, patch);
  },

  async patchItemsBulk(sessionId: string, itemIds: string[], patch: { userAction?: 'include' | 'skip'; clientId?: string; categoryOverride?: string }): Promise<void> {
    await apiClient.patch(`/import/session/${sessionId}/items/bulk`, { itemIds, ...patch });
  },

  async commit(sessionId: string): Promise<{ mode: 'sync' | 'async' }> {
    const r = await apiClient.post(`/import/session/${sessionId}/commit`, {});
    return r.data?.data;
  },

  async getProgress(sessionId: string): Promise<{
    state: string;
    totals: Record<string, number>;
    byStatus: Record<string, number>;
    completedAt: string | null;
  }> {
    const r = await apiClient.get(`/import/session/${sessionId}/progress`);
    return r.data?.data;
  },
};

// Friendly labels matched to backend taxonomy.ts
export const CATEGORY_LABELS: Record<string, string> = {
  tax_return: 'Tax Return',
  bas_ias: 'BAS / IAS',
  balance_sheet: 'Balance Sheet',
  profit_loss: 'Profit & Loss',
  trial_balance: 'Trial Balance',
  general_ledger: 'General Ledger',
  trust_deed: 'Trust Deed',
  company_constitution: 'Company Constitution',
  engagement_letter: 'Engagement Letter',
  id_document: 'ID Document',
  abn_acn_certificate: 'ABN / ACN Cert',
  insurance_policy: 'Insurance Policy',
  loan_agreement: 'Loan Agreement',
  mortgage: 'Mortgage',
  asset_register: 'Asset Register',
  property_settlement: 'Property Settlement',
  pension_super_statement: 'Pension / Super',
  ato_correspondence: 'ATO Correspondence',
  asic_correspondence: 'ASIC Correspondence',
  bank_statement: 'Bank Statement',
  bank_transaction_export: 'Bank Transactions',
  receipt: 'Receipt',
  email_export: 'Email Export',
  screenshot: 'Screenshot',
  correspondence: 'Correspondence',
  other: 'Other',
  unknown: 'Unknown',
};
